<script setup>
import { computed } from 'vue';

const props = defineProps({
  reactions: {
    type: Array,
    default: () => [],
  },
  orientation: {
    type: String,
    default: 'left',
    validator: value => ['left', 'right'].includes(value),
  },
});

const emit = defineEmits(['clickReaction']);

/**
 * Groups reactions by emoji and counts them
 * Input: [{ emoji: '👍', sender: {...} }, { emoji: '👍', sender: {...} }, { emoji: '❤️', sender: {...} }]
 * Output: [{ emoji: '👍', count: 2, senders: [...] }, { emoji: '❤️', count: 1, senders: [...] }]
 */
const groupedReactions = computed(() => {
  if (!props.reactions || !Array.isArray(props.reactions)) {
    return [];
  }

  const groups = {};

  props.reactions.forEach(reaction => {
    const emoji = reaction.emoji || reaction.reaction;
    if (!emoji) return;

    if (!groups[emoji]) {
      groups[emoji] = {
        emoji,
        count: 0,
        senders: [],
      };
    }

    groups[emoji].count += 1;

    // Store sender info for tooltip
    if (reaction.sender || reaction.senderName) {
      groups[emoji].senders.push({
        name: reaction.sender?.name || reaction.senderName || 'Unknown',
        id: reaction.sender?.id || reaction.senderId,
      });
    }
  });

  return Object.values(groups);
});

const hasReactions = computed(() => groupedReactions.value.length > 0);

const containerClass = computed(() => {
  return props.orientation === 'right' ? 'justify-end' : 'justify-start';
});

function getSendersTooltip(group) {
  if (!group.senders || group.senders.length === 0) {
    return group.emoji;
  }
  const names = group.senders.map(s => s.name).join(', ');
  return `${group.emoji} ${names}`;
}

function handleClick(group) {
  emit('clickReaction', group);
}
</script>

<template>
  <div class="flex gap-1 mt-1 flex-wrap" :class="containerClass">
    <template v-if="hasReactions">
      <button
        v-for="group in groupedReactions"
        :key="group.emoji"
        v-tooltip="getSendersTooltip(group)"
        type="button"
        class="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-n-alpha-2 hover:bg-n-alpha-3 border border-n-weak text-xs transition-colors cursor-pointer"
        @click="handleClick(group)"
      >
        <span class="text-sm leading-none">{{ group.emoji }}</span>
        <span
          v-if="group.count > 1"
          class="text-n-slate-11 text-xs font-medium"
        >
          {{ group.count }}
        </span>
      </button>
    </template>
  </div>
</template>
